import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider, createTheme, CssBaseline, IconButton, Tooltip } from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";

// --- Context
const ColorModeContext = createContext();

export const useColorMode = () => useContext(ColorModeContext);

// --- Main Theme Provider
export function CustomThemeProvider({ children }) {
  const [mode, setMode] = useState(localStorage.getItem("theme") || "light");

  const toggleColorMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("theme", next);
      return next;
    });
  };

  const theme = useMemo(() => {
    // Χρώματα palette & custom Alert colors
    const paletteColors = {
      primary: { main: "#49bc90", contrastText: "#fff" },
      secondary: { main: "#ffa84d", contrastText: "#fff" },
      success: { main: "#009e75", contrastText: "#fff" },
      background: {
        default: mode === "dark" ? "#181c21" : "#f3f6fa",
        paper: mode === "dark" ? "#23272e" : "#fff",
      },
      text: {
        primary: mode === "dark" ? "#f3f6fa" : "#202930",
        secondary: mode === "dark" ? "#adc4b2" : "#546e5d"
      }
    };

    // Alert styles ανά mode
    const alertStyles = {
      info: {
        background: mode === "dark"
          ? "linear-gradient(90deg, #222c2b 60%, #29413a 100%)"
          : "linear-gradient(90deg, #f3f6fa 70%, #e2fff3 100%)",
        color: mode === "dark" ? "#90e0cf" : "#1976d2",
        borderLeft: `5px solid #49bc90`,
      },
      error: {
        background: mode === "dark"
          ? "linear-gradient(90deg, #291818 60%, #392929 100%)"
          : "linear-gradient(90deg, #ffe5e5 70%, #ffe7d2 100%)",
        color: mode === "dark" ? "#ffb3b3" : "#d62a2a",
        borderLeft: `5px solid #ff5050`,
      },
      success: {
        background: mode === "dark"
          ? "linear-gradient(90deg, #133b27 60%, #174c33 100%)"
          : "linear-gradient(90deg, #eafff4 60%, #e2fff3 100%)",
        color: mode === "dark" ? "#49bc90" : "#009e75",
        borderLeft: `5px solid #49bc90`,
      },
      warning: {
        background: mode === "dark"
          ? "linear-gradient(90deg, #453415 60%, #544820 100%)"
          : "linear-gradient(90deg, #fff8e1 70%, #ffe3c0 100%)",
        color: mode === "dark" ? "#ffc069" : "#ed6c02",
        borderLeft: `5px solid #ffa84d`,
      }
    };

    return createTheme({
      palette: {
        mode,
        ...paletteColors
      },
      shape: { borderRadius: 16 },
      typography: {
        fontFamily: [
          "Nunito Sans",
          "Arial", "sans-serif"
        ].join(","),
        h6: {
          fontWeight: 800,
          letterSpacing: 1
        }
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: paletteColors.background.paper,
              color: paletteColors.text.primary,
              borderRadius: 18,
              boxShadow: mode === "dark" ? "0 4px 32px #161a20cc" : "0 4px 24px #c5e5d233"
            }
          }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 18,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 2px 12px 0 rgba(80,180,130,0.05)",
              '&:hover': {
                filter: "brightness(1.07)"
              }
            }
          }
        },
        MuiAppBar: {
          styleOverrides: {
            colorDefault: {
              background: mode === "dark"
                ? "linear-gradient(90deg, #23272e 70%, #2a313b 100%)"
                : "linear-gradient(90deg, #fff 80%, #f8eee2 100%)",
              color: paletteColors.primary.main,
              boxShadow: "0 4px 18px 0 rgba(80,180,130,0.09)"
            }
          }
        },
        // ----------- CUSTOM ALERT STYLES -----------
        MuiAlert: {
          styleOverrides: {
            standardInfo: {
              background: alertStyles.info.background,
              color: alertStyles.info.color,
              borderLeft: alertStyles.info.borderLeft,
              fontWeight: 600,
              fontSize: 17
            },
            standardError: {
              background: alertStyles.error.background,
              color: alertStyles.error.color,
              borderLeft: alertStyles.error.borderLeft,
              fontWeight: 600,
              fontSize: 17
            },
            standardSuccess: {
              background: alertStyles.success.background,
              color: alertStyles.success.color,
              borderLeft: alertStyles.success.borderLeft,
              fontWeight: 600,
              fontSize: 17
            },
            standardWarning: {
              background: alertStyles.warning.background,
              color: alertStyles.warning.color,
              borderLeft: alertStyles.warning.borderLeft,
              fontWeight: 600,
              fontSize: 17
            },
            root: {
              borderRadius: 18,
              boxShadow: "0 2px 18px #49bc9030",
              padding: "14px 22px"
            }
          }
        }
        // ----------- END ALERT -----------
      }
    });
  }, [mode]);

  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// --- Theme Toggle Button
export function ThemeToggleButton({ sx }) {
  const { mode, toggleColorMode } = useColorMode();
  return (
    <Tooltip title={mode === "dark" ? "Light mode" : "Dark mode"}>
      <IconButton
        onClick={toggleColorMode}
        color="inherit"
        sx={sx}
        size="large"
        aria-label="toggle dark mode"
      >
        {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Tooltip>
  );
}
